import useUser from "components/common/useUser";
import Icon from "components/Icons/Icon";
import { useAppDownloadUrl } from "components/common/useAppStoreLink";
import trackEvent from "components/tracking/trackEvent";
import trackFathom from "components/tracking/trackFathom";
import { TRAVEL_REEL_CONTENT } from "components/website/marketing/content/featurePageContent";
import { PlanGateModal, usePlanGate } from "components/website/components/planGate";
import { getTravelReelTranslations } from "components/website/marketing/i18n/pages/travelReel";
import { useBusinessContext, useOrganisation } from "context/BusinessContext";
import { t } from "i18n/index";
import { localizeHref, MarketingLangCode } from "i18n/urlLocale";
import React, { useState } from "react";
import { OsTypes, isMobile, osName } from "react-device-detect";
import { useAuthState } from "react-firebase-hooks/auth";
import useReactRouter from "use-react-router";
import firebase from "../../firebase";
import FAQSection from "../components/FAQSection";
import DownloadAppModal from "../components/DownloadAppModal";
import DownloadAppQrCard from "../components/DownloadAppQrCard";
import Footer from "../components/Footer";
import HowItWorksSection from "../components/HowItWorksSection";
import Navigation from "../components/navigation/Navigation";
import LanguageSwitcher from "../components/LanguageSwitcher";
import "../css/TravelTracker.scss";
import "../css/Website.scss";
import ReelVisual1 from "../img/screenshots/travelreel/travel-reel-customize.png";
import ReelHeroPoster from "../img/screenshots/travelreel/travel-reel-hero-poster.png";
import ReelHeroVideo from "../img/screenshots/travelreel/travel-reel-hero.mp4";
import ReelHeroVideoWebm from "../img/screenshots/travelreel/travel-reel-hero.webm";
import ReelVisual2 from "../img/screenshots/travelreel/travel-reel-share.png";

const S_PATH =
	"M17.3912 99.1864C14.9217 99.1864 12.9613 101.159 13.0092 103.593C13.0589 106.025 15.0991 108 17.5668 108C20.0363 108 21.9967 106.025 21.9488 103.593C21.8991 101.159 19.8589 99.1864 17.3912 99.1864ZM65.3336 33.3614C76.1235 30.4927 80.4221 24.1043 80.431 16.7419C80.4434 6.75566 72.3519 0 59.0871 0C38.1939 0 0 22.4846 0 43.1187C0 59.3036 21.0387 64.5548 35.3004 67.8972C49.5639 71.2377 60.588 74.2732 60.588 78.2187C60.588 82.1625 48.0081 91.0523 27.7464 97.4815C25.575 98.2036 24.3065 100.144 24.9097 101.816C25.5147 103.487 27.7145 104.079 29.9356 103.533C38.3749 101.452 78.201 92.3474 78.201 74.4204C78.201 56.4935 35.2862 49.1612 35.2862 45.9005C35.2862 42.5528 56.6017 35.6819 65.3336 33.3614Z";

interface TravelReelPageProps {
	lang?: string;
}

export default function TravelReelPage({ lang }: TravelReelPageProps) {
	const [user] = useAuthState(firebase.auth);
	const { user: stipplUser, initializing } = useUser();
	const { history, match } = useReactRouter();
	const [showDownloadModal, setShowDownloadModal] = useState(false);
	const { isBusinessMode: isBusiness } = useBusinessContext();
	const { organisation, loading: orgLoading } = useOrganisation();
	const translations = getTravelReelTranslations(lang || "en");
	// 123yb691qz1: every /register anchor on this page opens the signup modal in place instead
	// of navigating; `mobile: "store"` keeps 123yb691bdn's rule that a phone goes to the store
	// instead. `match.path` reuses the label `handleTrack("signup")` already tracked under.
	const gate = usePlanGate(match.path, "store");

	React.useEffect(() => {
		if (!user || !stipplUser?.createdAt) return;
		if (isBusiness) {
			if (orgLoading) return;
			const orgName = organisation?.name;
			if (orgName) history.push(`/${encodeURIComponent(orgName)}`);
			return;
		}
		const hasValidUsername = !!stipplUser?.username && !stipplUser?.username?.includes("StipplUser");
		if (hasValidUsername) {
			history.push("/home");
		} else {
			history.push("/onboarding");
		}
	}, [user, initializing, stipplUser, isBusiness, organisation, orgLoading]);

	const handleTrack = (type: "signup" | "download" | "appstore" | "googleplay") => {
		if (type === "appstore") {
			trackFathom("FD21GVOP");
			trackEvent("click_appstore_cta", { path: match.path }, "marketing_site");
		} else if (type === "googleplay") {
			trackFathom("BNQKH0MH");
			trackEvent("click_googleplay_cta", { path: match.path }, "marketing_site");
		} else if (osName === OsTypes.Android && type === "download") {
			trackFathom("BNQKH0MH");
			trackEvent("click_googleplay_cta", { path: match.path }, "marketing_site");
		} else if (osName === OsTypes.IOS && type === "download") {
			trackFathom("FD21GVOP");
			trackEvent("click_appstore_cta", { path: match.path }, "marketing_site");
		} else {
			trackEvent("click_signup_cta", { path: match.path }, "marketing_site");
			trackFathom("E4NT4HWN");
		}
	};

	// 123yb6937h3: both "Download the app" CTAs below resolve their href through this -- always a
	// real, attributed OneLink, including on the very first render before mount (see
	// useAppDownloadUrl's doc comment in useAppStoreLink.ts). Previously these were <button
	// onClick={setShowDownloadModal(true)}> with no href at all, so the server HTML had a dead
	// control -- confirmed on production by QA's curl retest of 123yb6937h3.
	const appDownloadUrl = useAppDownloadUrl();

	return (
		<div className="travel-tracker-page" id="stippl-travel-reel">
			<Navigation impact={true} langMenu={<LanguageSwitcher type="page" />} />

			{/* HERO */}
			<section className="hr-hero">
				<svg className="hero-s-mark" viewBox="0 0 81 108" fill="none" xmlns="http://www.w3.org/2000/svg">
					<path d={S_PATH} fill="#ffffff" />
				</svg>
				<svg className="hero-s-mark-left" viewBox="0 0 81 108" fill="none" xmlns="http://www.w3.org/2000/svg">
					<path d={S_PATH} fill="#ffffff" />
				</svg>
				<div className="container__inner hr-hero__inner">
					<div className="hr-hero__content">
						<div className="hero-chip">{t(translations, "travelReel.hero.chip", "Generate 3D travel videos")}</div>
						<h1>
							{t(translations, "travelReel.hero.titleLine1", "Turn your trips into 3D travel reels.")}
							<br />
							<span className="hero__accent">{t(translations, "travelReel.hero.titleAccent", "Share every journey.")}</span>
						</h1>
						<p className="hr-hero__desc">
							{t(
								translations,
								"travelReel.hero.desc",
								"Generate a stunning 3D video of your entire route and photos, automatically. No editing, no software, no timeline. Plan a trip, add your photos, and your reel is ready in minutes."
							)}
						</p>
						<div className="hero-ctas">
							{/* 123yb6937h3: on a phone the anchor is simply followed -- OneLink does the OS split
								(App Store vs Play Store) server-side, which is the ticket's acceptance criterion.
								On desktop we still want DownloadAppModal's QR code as the affordance, so the click
								handler -- browser-only, unlike a render-time device check -- prevents the default
								navigation and opens the modal instead. */}
							<a
								href={appDownloadUrl}
								className="hero-glass-btn"
								onClick={(e) => {
									handleTrack("download");
									if (!isMobile) {
										e.preventDefault();
										setShowDownloadModal(true);
									}
								}}
							>
								<div className="hero-glass-btn__icon">
									<svg width="16" height="21" viewBox="0 0 81 108" fill="none">
										<path d={S_PATH} fill="#fff" />
									</svg>
								</div>
								<span className="hero-glass-btn__label">{t(translations, "travelReel.cta.downloadApp", "Download the app")}</span>
							</a>
							<a
								href="/register"
								className="hero-web-link"
								onClick={(e) => {
									e.preventDefault();
									gate.requestPlan();
								}}
							>
								{t(translations, "travelReel.cta.startOnWeb", "Start on web →")}
							</a>
						</div>
					</div>
					<div
						className="hr-hero__phone hr-hero__phone--front"
						style={{ aspectRatio: "1 / 1", display: "flex", alignItems: "center", justifyContent: "center" }}
					>
						<video
							poster={ReelHeroPoster}
							style={{ background: "transparent", borderRadius: "2.25rem", height: "100%", width: "auto", maxWidth: "100%", objectFit: "contain" }}
							autoPlay
							muted
							loop
							playsInline
							preload="none"
							aria-label={t(translations, "travelReel.hero.videoAlt", "Travel Reel preview")}
						>
							<source src={ReelHeroVideoWebm} type="video/webm" />
							<source src={ReelHeroVideo} type="video/mp4" />
						</video>
					</div>
				</div>
			</section>

			{/* FEATURES INTRO */}
			<section className="features-intro">
				<div className="section-wrapper">
					<div className="features-intro-header">
						<div className="section-chip chip-purple" style={{ margin: "0 auto 16px" }}>
							{t(translations, "travelReel.featuresIntro.chip", "Why Travel Reels")}
						</div>
						<h2>{t(translations, "travelReel.featuresIntro.title", "Every trip you plan already has a reel waiting")}</h2>
						<p>
							{t(
								translations,
								"travelReel.featuresIntro.desc",
								"No editing, no software: just plan a trip, add your photos, and your reel is ready in minutes."
							)}
						</p>
					</div>
					<div className="features-grid">
						<div className="feature-card">
							<div className="feature-card-icon icon-purple">
								<Icon name="lightning" size={22} pathProps={{ stroke: "var(--main4)" }} />
							</div>
							<h3>{t(translations, "travelReel.features.card1.title", "Zero effort")}</h3>
							<p>{t(translations, "travelReel.features.card1.desc", "It's made from the trip you already planned: nothing to edit, nothing to make.")}</p>
						</div>
						<div className="feature-card">
							<div className="feature-card-icon icon-purple">
								<Icon name="globe" size={22} fill="var(--main4)" />
							</div>
							<h3>{t(translations, "travelReel.features.card2.title", "3D globe animation")}</h3>
							<p>{t(translations, "travelReel.features.card2.desc", "Your journey traced across a cinematic 3D map, zooming stop to stop.")}</p>
						</div>
						<div className="feature-card">
							<div className="feature-card-icon icon-green">
								<Icon name="addPhoto" size={22} pathProps={{ fill: "var(--main1)" }} />
							</div>
							<h3>{t(translations, "travelReel.features.card3.title", "Your own photos")}</h3>
							<p>{t(translations, "travelReel.features.card3.desc", "Drop journal photos in, they appear at the right city.")}</p>
						</div>
						<div className="feature-card">
							<div className="feature-card-icon icon-amber">
								<Icon name="phone" size={22} pathProps={{ stroke: "var(--main3)" }} />
							</div>
							<h3>{t(translations, "travelReel.features.card4.title", "Made for social")}</h3>
							<p>{t(translations, "travelReel.features.card4.desc", "Formatted for Instagram Reels, TikTok & YouTube Shorts.")}</p>
						</div>
						<div className="feature-card">
							<div className="feature-card-icon icon-pink">
								<Icon name="refresh-cycle" size={22} fill="var(--main2)" />
							</div>
							<h3>{t(translations, "travelReel.features.card5.title", "Before & after")}</h3>
							<p>{t(translations, "travelReel.features.card5.desc", "Hype the trip before you go, relive it when you're back.")}</p>
						</div>
						<div className="feature-card">
							<div className="feature-card-icon icon-green">
								<Icon name="gift" size={22} pathProps={{ stroke: "var(--main1)" }} />
							</div>
							<h3>{t(translations, "travelReel.features.card6.title", "Free & watermark-free")}</h3>
							<p>{t(translations, "travelReel.features.card6.desc", "Every reel, no cost.")}</p>
						</div>
					</div>
				</div>
			</section>

			{/* FEATURE: BRING YOUR ADVENTURES BACK TO LIFE */}
			<section className="bg-white">
				<div className="section-wrapper">
					<div className="feature-split reverse">
						<div className="feature-copy">
							<div className="section-chip chip-purple">{t(translations, "travelReel.hero.chip", "Generate 3D travel videos")}</div>
							<h2>{t(translations, "travelReel.bringToLife.title", "Bring your travel adventures back to life")}</h2>
							<p>
								{t(
									translations,
									"travelReel.bringToLife.desc",
									"Turn your photos and memories into stunning travel videos in just a few clicks. Whether it's a highlight reel or a visual diary, your 3D travel video brings the whole journey back."
								)}
							</p>
							<div className="feature-list">
								<div className="feature-list-item">
									<div className="feature-list-icon purple">
										<Icon name="addPhoto" size={18} fill="var(--main4)" />
									</div>
									<div className="feature-list-copy">
										<h4>{t(translations, "travelReel.bringToLife.item1.title", "Upload your favourite photos")}</h4>
										<p>{t(translations, "travelReel.bringToLife.item1.desc", "Add them to your journals, that's all it takes.")}</p>
									</div>
								</div>
								<div className="feature-list-item">
									<div className="feature-list-icon purple">
										<Icon name="lightning" size={18} pathProps={{ stroke: "var(--main4)" }} />
									</div>
									<div className="feature-list-copy">
										<h4>{t(translations, "travelReel.bringToLife.item2.title", "Ready in minutes")}</h4>
										<p>{t(translations, "travelReel.bringToLife.item2.desc", "No editing, no software.")}</p>
									</div>
								</div>
								<div className="feature-list-item">
									<div className="feature-list-icon purple">
										<Icon name="map--outlined" size={18} fill="var(--main4)" />
									</div>
									<div className="feature-list-copy">
										<h4>{t(translations, "travelReel.bringToLife.item3.title", "See the whole route")}</h4>
										<p>
											{t(
												translations,
												"travelReel.bringToLife.item3.desc",
												"A visual map of your journey, so friends and family instantly get where you're going."
											)}
										</p>
									</div>
								</div>
							</div>
							<a
								href="/register"
								className="feature-cta-link"
								onClick={(e) => {
									e.preventDefault();
									gate.requestPlan();
								}}
							>
								{t(translations, "travelReel.bringToLife.cta", "Start your travel reel →")}
							</a>
						</div>
						<div className="feature-visual">
							<img src={ReelVisual1} alt={t(translations, "travelReel.bringToLife.imgAlt", "Travel reel screenshot")} className="feature-screenshot" />
						</div>
					</div>
				</div>
			</section>

			{/* FEATURE: SHARE BEFORE & AFTER */}
			<section className="bg-ice">
				<div className="section-wrapper">
					<div className="feature-split">
						<div className="feature-copy">
							<div className="section-chip chip-purple">{t(translations, "travelReel.shareSection.chip", "Save & share on your socials")}</div>
							<h2>{t(translations, "travelReel.shareSection.title", "Share your travels before and after you go")}</h2>
							<p>
								{t(
									translations,
									"travelReel.shareSection.desc",
									"Your travel videos deserve an audience. Share them before you embark to get everyone excited, or upload your best moments once you're back to relive the journey, and refresh your reel as you go."
								)}
							</p>
							<div className="feature-list">
								<div className="feature-list-item">
									<div className="feature-list-icon purple">
										<Icon name="share--outlined" size={18} fill="var(--main4)" />
									</div>
									<div className="feature-list-copy">
										<h4>{t(translations, "travelReel.shareSection.item1.title", "Export anywhere")}</h4>
										<p>{t(translations, "travelReel.shareSection.item1.desc", "Whatsapp, Instagram Reels, TikTok & Shorts.")}</p>
									</div>
								</div>
								<div className="feature-list-item">
									<div className="feature-list-icon purple">
										<Icon name="refresh-cycle" size={18} fill="var(--main4)" />
									</div>
									<div className="feature-list-copy">
										<h4>{t(translations, "travelReel.shareSection.item2.title", "Update anytime")}</h4>
										<p>{t(translations, "travelReel.shareSection.item2.desc", "Add stops and photos, regenerate whenever you like.")}</p>
									</div>
								</div>
								<div className="feature-list-item">
									<div className="feature-list-icon purple">
										<Icon name="gift" size={18} pathProps={{ stroke: "var(--main4)" }} />
									</div>
									<div className="feature-list-copy">
										<h4>{t(translations, "travelReel.shareSection.item3.title", "Tag @stippl.io, earn rewards")}</h4>
										<p>{t(translations, "travelReel.shareSection.item3.desc", "Get Stippl points for perks like free eSIMs when you share your reel.")}</p>
									</div>
								</div>
							</div>
							<a
								href="/register"
								className="feature-cta-link"
								onClick={(e) => {
									e.preventDefault();
									gate.requestPlan();
								}}
							>
								{t(translations, "travelReel.shareSection.cta", "Share your reel →")}
							</a>
						</div>
						<div className="feature-visual">
							<img
								src={ReelVisual2}
								alt={t(translations, "travelReel.shareSection.imgAlt", "Sharing a travel reel screenshot")}
								className="feature-screenshot"
							/>
						</div>
					</div>
				</div>
			</section>

			{/* HOW IT WORKS */}
			<HowItWorksSection
				theme="purple"
				tinted
				badge={t(translations, "travelReel.howItWorks.badge", "Fully automatic")}
				title={t(translations, "travelReel.howToUse.name", TRAVEL_REEL_CONTENT.howToUse.name)}
				steps={TRAVEL_REEL_CONTENT.howToSteps.map((step, index) => ({
					...step,
					name: t(translations, `travelReel.howToSteps.${index}.name`, step.name),
					text: t(translations, `travelReel.howToSteps.${index}.text`, step.text),
				}))}
				ctaText={t(translations, "travelReel.howItWorks.ctaText", "Generate your reel for free →")}
				onCtaClick={() => handleTrack("signup")}
			/>

			{/* PRO CALLOUT */}
			<section className="pro-callout-section">
				<div className="section-wrapper">
					<div className="pro-callout-block">
						<div className="pro-callout-left">
							<div className="pro-callout-label">Stippl Pro</div>
							<h3>{t(translations, "travelReel.pro.title", "Make your reel truly yours")}</h3>
							<p>{t(translations, "travelReel.pro.desc", "Reels are free. Pro unlocks the customisation that makes yours stand out.")}</p>
							<div className="pro-features-list">
								<div className="pro-feature-row">
									<div className="pro-feature-check">
										<svg width="12" height="12" viewBox="0 0 12 12" fill="none">
											<polyline points="2,6 5,9 10,3" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
										</svg>
									</div>
									<span>
										<strong>{t(translations, "travelReel.pro.feature1Strong", "Up to 10 photos per destination")}</strong>
										{t(translations, "travelReel.pro.feature1Text", ", fill every stop with your best shots")}
									</span>
								</div>
								<div className="pro-feature-row">
									<div className="pro-feature-check">
										<svg width="12" height="12" viewBox="0 0 12 12" fill="none">
											<polyline points="2,6 5,9 10,3" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
										</svg>
									</div>
									<span>
										<strong>{t(translations, "travelReel.pro.feature2Strong", "Custom colours")}</strong>
										{t(translations, "travelReel.pro.feature2Text", ", match the reel's look to your trip's vibe")}
									</span>
								</div>
								<div className="pro-feature-row">
									<div className="pro-feature-check">
										<svg width="12" height="12" viewBox="0 0 12 12" fill="none">
											<polyline points="2,6 5,9 10,3" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
										</svg>
									</div>
									<span>
										<strong>{t(translations, "travelReel.pro.feature3Strong", "Different map styles")}</strong>
										{t(translations, "travelReel.pro.feature3Text", ": switch between Satellite, Adventure and Explorer views")}
									</span>
								</div>
							</div>
						</div>
						<div className="pro-price-card">
							<svg className="pro-price-card-smark" viewBox="0 0 81 108" fill="none" xmlns="http://www.w3.org/2000/svg">
								<path d={S_PATH} fill="white" />
							</svg>
							<div className="pro-price-label">{t(translations, "travelReel.pro.priceLabel", "Starting from")}</div>
							<div className="pro-price-amount">€3</div>
							<div className="pro-price-period">{t(translations, "travelReel.pro.pricePeriod", "per month")}</div>
							<a href={localizeHref("/pro", lang as MarketingLangCode)} className="pro-price-btn">
								<svg width="14" height="18" viewBox="0 0 81 108" fill="none">
									<path d={S_PATH} fill="currentColor" />
								</svg>
								{t(translations, "travelReel.pro.priceBtn", "Get Pro →")}
							</a>
							<div className="pro-price-note">{t(translations, "travelReel.pro.priceNote", "Cancel anytime · No commitment")}</div>
						</div>
					</div>
				</div>
			</section>

			{/* FAQ */}
			<section>
				<div className="section-wrapper">
					<FAQSection
						heading={t(translations, "travelReel.faq.heading", "Frequently asked questions about Stippl Travel Reels")}
						items={TRAVEL_REEL_CONTENT.faqItems.map((item, index) => ({
							...item,
							question: t(translations, `travelReel.faqItems.${index}.question`, item.question),
							answer: t(translations, `travelReel.faqItems.${index}.answer`, item.answer),
						}))}
					/>
				</div>
			</section>

			{/* CTA SECTION */}
			<section className="cta-section">
				<svg className="cta-s" viewBox="0 0 81 108" fill="none">
					<path d={S_PATH} fill="#ffffff" />
				</svg>
				<svg className="cta-s-right" viewBox="0 0 81 108" fill="none">
					<path d={S_PATH} fill="#ffffff" />
				</svg>
				<div className="cta-inner">
					<div className="cta-chip">{t(translations, "travelReel.hero.chip", "Generate 3D travel videos")}</div>
					<h2>
						{t(translations, "travelReel.hero.titleLine1", "Turn your trips into 3D travel reels.")}
						<br />
						{t(translations, "travelReel.hero.titleAccent", "Share every journey.")}
					</h2>
					<p className="cta-sub">
						{t(
							translations,
							"travelReel.ctaStrip.sub",
							"Free for 1.5 million+ travellers. Plan a trip, add your photos, and your 3D travel reel is ready in minutes: no editing, no software."
						)}
					</p>
					<div className="cta-btns">
						{/* Same fix as the hero CTA above (123yb6937h3): a real href on the server render, store
							link on mobile, modal-with-QR on desktop. */}
						<a
							href={appDownloadUrl}
							className="hero-glass-btn"
							onClick={(e) => {
								handleTrack("download");
								if (!isMobile) {
									e.preventDefault();
									setShowDownloadModal(true);
								}
							}}
						>
							<div className="hero-glass-btn__icon">
								<svg width="16" height="21" viewBox="0 0 81 108" fill="none">
									<path d={S_PATH} fill="#fff" />
								</svg>
							</div>
							<span className="hero-glass-btn__label">{t(translations, "travelReel.cta.downloadApp", "Download the app")}</span>
						</a>
						<a
							href="/register"
							className="btn btn-outline-white"
							onClick={(e) => {
								e.preventDefault();
								gate.requestPlan();
							}}
						>
							{t(translations, "travelReel.cta.startOnWeb", "Start on web →")}
						</a>
					</div>
				</div>
			</section>

			<Footer />
			<DownloadAppQrCard />
			<DownloadAppModal isOpen={showDownloadModal} onClose={() => setShowDownloadModal(false)} location={match.path} />
			<PlanGateModal gate={gate} title="Create a free account to start planning" />
		</div>
	);
}
